import React, { useState, useRef, useEffect, useMemo } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import ErrorTable from './../Tables/ErrorTable';
import Button from '@material-ui/core/Button';
import XLSX from 'xlsx';
import moment from 'moment';
import { couponsValidationText, allowedCourses } from './../constants';

const EXPIRY_DATE_FORMAT = 'MM/DD/YYYY HH:mm:ss';
const BEFORESIGNUP_DATE_FORMAT = 'MM/DD/YYYY';
const BULK_COUPON_TEMPLATE_DATA = [
  {
    couponCode: 'TESTCOUPON',
    goalType: 'web3',
    planType: 'pro_live',
    subPlanType: null,
    'discountAmount (in paise)': 100000,
    'expiryDate (mm/dd/yyyy hh:mm:ss)': '05/09/2022 17:30:00',
    'beforeSignupDate (mm/dd/yyyy)': '05/09/2022',
    owner: 'someone@almabetter.com',
  },
];

export default function BulkCreateCouponDialog({
  allCoupons,
  bulkCouponCreation,
  open,
  handleCloseButton,
}) {
  const [file, setFile] = useState(null);
  const [validatedCoupons, setValidatedCoupons] = useState([]);
  const [validationSheetError, setValidationSheetError] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (file.length !== 0) {
      handleFile(file[0]);
    }
  };
  const handleFile = (file) => {
    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;
    reader.onload = (e) => {
      const bstr = e.target.result;
      const wb = XLSX.read(bstr, {
        type: rABS ? 'binary' : 'array',
        cellDates: true,
        dateNF: 'mm/dd/yyyy',
      });
      // Get first worksheet
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      // Convert array of arrays
      const sheetData = XLSX.utils.sheet_to_json(ws, { header: 1 });
      // validate and format data
      formatDataAndUpload(sheetData);
    };
    if (rABS) reader.readAsBinaryString(file);
    else reader.readAsArrayBuffer(file);
  };

  const doesCouponsHaveErrors = (coupons) => {
    return coupons.some((c) => c.validationError.length);
  };
  const formatDataAndUpload = (sheetRows) => {
    const sheetValidationError = validateSheet(sheetRows);
    if (sheetValidationError) {
      setValidationSheetError(sheetValidationError);
      return;
    }
    sheetRows = sheetRows.slice(1);
    const coupons = sheetRows.map((row) => ({
      couponCode: row[0],
      goalType: row[1],
      planType: row[2],
      subPlanType: row[3],
      discountAmount:
        typeof row[4] === 'string' && row[4].trim() ? Number(row[4]) : row[4],
      expiryDate:
        row[5] instanceof Date
          ? row[5]
          : typeof row[5] === 'string' &&
            moment(row[5], EXPIRY_DATE_FORMAT).isValid()
          ? moment(row[5], EXPIRY_DATE_FORMAT).toDate()
          : row[5],
      beforeSignupDate:
        row[6] instanceof Date
          ? row[6]
          : typeof row[6] === 'string' &&
            moment(row[6], BEFORESIGNUP_DATE_FORMAT).isValid()
          ? moment(row[6], BEFORESIGNUP_DATE_FORMAT).toDate()
          : row[6],
      owner:
        row[7] && typeof row[7] === 'string' && row[7].trim()
          ? row[7].trim()
          : undefined,
    }));
    const couponWithValidationErrors = coupons.map((coupon) => ({
      ...coupon,
      validationError: validateRawCouponRow(coupon, coupons),
    }));
    setValidatedCoupons(couponWithValidationErrors);
    if (!doesCouponsHaveErrors(couponWithValidationErrors)) {
      bulkUpload(couponWithValidationErrors);
    }
  };

  const validateSheet = (sheetRows) => {
    if (sheetRows.length <= 1) {
      return 'Empty sheet';
    } else if (!sheetRows[0] || sheetRows[0].length !== 8) {
      return 'Data Not Valid';
    }
    return null;
  };

  const validateRawCouponRow = (rawCoupon, allCouponsToBeUploaded) => {
    const errors = [];
    if (!rawCoupon.couponCode)
      errors.push('couponCode' + couponsValidationText.EMPTY);

    if (!rawCoupon.goalType)
      errors.push('goalType' + couponsValidationText.EMPTY);
    else if (!allowedCourses.includes(rawCoupon.goalType)) {
      errors.push(couponsValidationText.INVALID_GOAL_TYPE);
    }

    if (!rawCoupon.discountAmount)
      errors.push('discountAmount' + couponsValidationText.EMPTY);
    else if (!Number.isInteger(rawCoupon.discountAmount)) {
      errors.push('Discount Amount should be in Integer');
    }

    if (!rawCoupon.expiryDate)
      errors.push('Expiry Date' + couponsValidationText.EMPTY);
    else if (!(rawCoupon.expiryDate instanceof Date)) {
      errors.push('Expiry Date should be in format (mm/dd/yyyy hh:mm:ss)');
    }

    if (!rawCoupon.beforeSignupDate)
      errors.push('beforeSignupDate' + couponsValidationText.EMPTY);
    else if (!(rawCoupon.beforeSignupDate instanceof Date)) {
      errors.push('beforeSignupDate should be in format (mm/dd/yyyy)');
    }

    if (!rawCoupon.subPlanType)
      errors.push('subPlanType' + couponsValidationText.EMPTY);

    const couponIndex = allCoupons.findIndex(
      (existingCoupon) =>
        existingCoupon.couponCode === rawCoupon.couponCode &&
        existingCoupon.goalType === rawCoupon.goalType &&
        existingCoupon.subPlanType === rawCoupon.subPlanType
    );
    if (couponIndex > -1) {
      errors.push('Already exist');
    }

    const duplicateCoupons = allCouponsToBeUploaded.filter(
      (c) =>
        c.couponCode === rawCoupon.couponCode &&
        c.goalType === rawCoupon.goalType &&
        c.subPlanType === rawCoupon.subPlanType
    );

    if (duplicateCoupons.length >= 2) {
      errors.push('Duplicate Coupons');
    }

    return errors;
  };

  const bulkUpload = (coupons) => {
    const dataToUpload = coupons.map((couponData) => {
      return {
        couponCode: couponData.couponCode,
        goalType: couponData.goalType,
        planType: couponData.planType,
        subPlanType: couponData.subPlanType,
        discountAmount: Number(couponData.discountAmount),
        expiryDate: couponData.expiryDate.toISOString(),
        beforeSignupDate: couponData.beforeSignupDate.toISOString(),
        owner: couponData.owner,
      };
    });
    bulkCouponCreation(dataToUpload);
  };

  const bulkCouponTemplate = useMemo(() => {
    const worksheet = XLSX.utils.json_to_sheet(BULK_COUPON_TEMPLATE_DATA);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new File([csv], { type: 'text/csv' });
    return URL.createObjectURL(blob);
  }, []);

  useEffect(() => {
    setValidationSheetError(null);
    setValidatedCoupons([]);
  }, [file]);
  const areCouponsErroneous = doesCouponsHaveErrors(validatedCoupons);
  const isUploadButtonDisabled =
    !file || !file.length || validationSheetError || areCouponsErroneous;
  return (
    <Dialog onClose={handleCloseButton} open={open} fullWidth={true}>
      <DialogTitle id='simple-dialog-title' style={{ textAlign: 'center' }}>
        Bulk Coupon Creation
      </DialogTitle>
      <DialogContent>
        <div className='flex flex-col items-center py-2'>
          <div className='mb-4 text-sm' style={{ color: 'var(--text-secondary)' }}>Supported Files: CSV, XLSX</div>
          
          <div className='flex gap-3 mb-6'>
            <button
              onClick={() => fileRef.current.click()}
              className='px-4 py-2 rounded-md text-sm font-semibold cursor-pointer transition-all duration-150'
              style={{
                backgroundColor: 'var(--active-background)',
                color: 'var(--active-text)',
                border: '1px solid var(--border-main)',
              }}
            >
              Choose File
            </button>
            <input
              onChange={(e) => setFile(e.target.files)}
              type='file'
              ref={fileRef}
              hidden={true}
              accept='.csv, .xlsx'
            />
            <a href={bulkCouponTemplate} download='BulkAddCouponTemplate.csv'>
              <button
                className='px-4 py-2 rounded-md text-sm font-semibold cursor-pointer transition-all duration-150'
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-main)',
                }}
              >
                Download Template
              </button>
            </a>
          </div>

          {file && file.length && (
            <div className='mb-4 text-sm font-medium' style={{ color: '#10B981' }}>
              File Added: <b>{file[0].name}</b>
            </div>
          )}
          {validationSheetError && (
            <div className='mb-4 text-sm' style={{ color: '#EF4444' }}>{validationSheetError}</div>
          )}
          {areCouponsErroneous && (
            <div className='my-4 w-full'>
              <ErrorTable validatedCoupons={validatedCoupons} />
            </div>
          )}

          <div className='flex gap-3 mt-4'>
            <Button onClick={handleCloseButton} color='primary' variant='text'>
              Cancel
            </Button>
            <button
              className={`px-5 py-2 rounded-md text-sm font-semibold cursor-pointer transition-all duration-150 ${
                isUploadButtonDisabled ? 'opacity-40 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: isUploadButtonDisabled ? undefined : 'var(--primary-main)',
                color: isUploadButtonDisabled ? undefined : 'var(--primary-contrast)',
                border: isUploadButtonDisabled ? '1px solid var(--border-main)' : 'none',
              }}
              disabled={isUploadButtonDisabled}
              onClick={handleUpload}
            >
              Upload File
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
